// src/components/resources/tools/generators/SubtitleOverlay/FormField.tsx
'use client'

import { useCallback } from 'react'
import { clsx } from 'clsx'
import Image from 'next/image'
import { isEnhancedValidation } from '~/lib/resources/types/tools/generator'
import { Input } from '~/components/Input'
import { TremorSlider } from '~/components/ui/tremor-slider'
import { Dropzone, DropzoneEmptyState } from '~/components/ui/primitives/dropzone'
import type { FormFieldProps } from './types'
import { safeStringValue, safeNumberValue } from './form-value-converters'

/**
 * Form field component that renders different input types based on field configuration
 */
export function FormField({ field, showValidationError, value, onChange, setLocalValidationErrors }: FormFieldProps): JSX.Element {
  const handleChange = useCallback((newValue: unknown) => {
    onChange(newValue)
  }, [onChange])

  /**
   * Gets validation constraints for number fields
   */
  const getNumberConstraints = useCallback(() => {
    const validation = field.validation
    if (!validation) return { min: undefined, max: undefined, step: undefined }

    let min: number | undefined
    let max: number | undefined
    let step: number | undefined

    if (isEnhancedValidation(validation)) {
      min = validation.enhancedMin?.value ?? (validation as { min?: number }).min
      max = validation.enhancedMax?.value ?? (validation as { max?: number }).max
      step = validation.stepConstraint?.value ?? (validation as { step?: number }).step
    } else {
      const legacyValidation = validation as { min?: number; max?: number; step?: number }
      min = legacyValidation.min
      max = legacyValidation.max
      step = legacyValidation.step
    }

    return { min, max, step }
  }, [field.validation])

  /**
   * Handles number input changes with proper validation and constraints
   */
  const handleNumberChange = useCallback((inputValue: string) => {
    // Allow empty string for clearing the field
    if (inputValue === '') {
      handleChange('')
      return
    }

    // Allow partial decimal inputs like "1." or "0."
    if (inputValue.endsWith('.') && inputValue.split('.').length === 2) {
      handleChange(inputValue)
      return
    }

    // Parse the number
    const numValue = parseFloat(inputValue)

    // Check if it's a valid number
    if (!isNaN(numValue)) {
      // Don't apply constraints during typing - let user type freely
      // Constraints will be applied on blur
      handleChange(numValue)
    }
  }, [handleChange])

  // --- Begin: Rollback UploadThing for file fields to client-side file input ---
  // We'll use a file input and FileReader to read the image as a data URL, and store it in form state.

  /**
   * Handles file drop or selection for the Dropzone.
   * Reads the image as a data URL and updates form state.
   *
   * @param {File[]} acceptedFiles - Array of accepted files (should be length 1 for single image)
   */
  const handleDropzoneUpload = useCallback(
    (acceptedFiles: File[]): void => {
      const file = acceptedFiles?.[0]
      if (!file) return

      // Only allow image files for safety
      if (!file.type.startsWith('image/')) {
        setLocalValidationErrors((prev: Record<string, string>) => ({
          ...prev,
          [field.id]: 'Please select a valid image file.'
        }))
        return
      }

      // Read the file as a data URL for preview and storage in form state
      const reader = new FileReader()
      reader.onload = e => {
        // Store the data URL in form state
        onChange(e.target?.result as string)
        // Clear any previous error for this field
        setLocalValidationErrors(prev => {
          const { [field.id]: _, ...rest } = prev
          return rest
        })
      }
      reader.onerror = () => {
        setLocalValidationErrors((prev: Record<string, string>) => ({
          ...prev,
          [field.id]: 'Failed to read file. Please try again.'
        }))
      }
      reader.readAsDataURL(file)
    },
    [onChange, setLocalValidationErrors, field.id]
  )
  // --- End: Rollback UploadThing for file fields ---

  switch (field.type) {
    case 'text':
    case 'textarea': {
      return (
        <textarea
          value={safeStringValue(value)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          rows={field.type === 'textarea' ? 4 : 1}
          className={clsx(
            'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
            showValidationError
              ? 'border-red-300 dark:border-red-600'
              : 'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
          )}
        />
      )
    }

      case 'number': {
        const { min, max, step } = getNumberConstraints()
        const hasSliderMode = field.inputModes?.secondary === 'slider' || field.inputModes?.secondary === 'stepper'

        // Use slider for range inputs with defined min/max
        if (hasSliderMode && min !== undefined && max !== undefined) {
          const parsedValue: number =
            typeof value === 'number'
              ? value
              : Number.parseFloat(safeNumberValue(value))
          const numValue: number =
            Number.isFinite(parsedValue) ? parsedValue : (min ?? 0)
          // Preserving the previous behaviour means treating NaN as "use the minimum" while still allowing 0 to remain selectable.

          // Get step constraint from field configuration for proper decimal handling
          const stepConstraint = field.validation?.stepConstraint?.value
          const effectiveStep: number = stepConstraint ?? step ?? 0.1

          return (
            <TremorSlider
              value={[numValue]}
              onValueChange={(values: number[]) => {
                const [sliderValue] = values
                const nextValue = sliderValue ?? min ?? 0
                handleChange(nextValue)
              }}
              min={min}
              max={max}
              step={effectiveStep}
              showValue={true}
              showSteppers={true}
              showValidation={true}
              valueLabel={field.id === 'lineHeight' ? 'x' : field.id === 'maxWidth' ? '%' : undefined}
            />
          )
        }

        // Use Tremor Input component for regular number inputs
        return (
          <Input
            type="number"
            value={safeNumberValue(value)}
            onChange={(e) => handleNumberChange(e.target.value)}
            onBlur={(e) => {
              // On blur, ensure we have a valid number or empty string
              const inputValue = e.target.value
              if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
                const numValue = parseFloat(inputValue)
                let constrainedValue = numValue

                // Apply constraints
                if (min !== undefined && numValue < min) {
                  constrainedValue = min
                }
                if (max !== undefined && numValue > max) {
                  constrainedValue = max
                }

                // Only update if the value actually changed
                if (constrainedValue !== numValue) {
                  handleChange(constrainedValue)
                }
              }
            }}
            placeholder={field.placeholder}
            min={min}
            max={max}
            step={step ?? 'any'}
            enableStepper={true}
            hasError={showValidationError}
            className="w-full"
          />
        )
      }

    case 'select': {
      return (
        <select
          value={safeStringValue(value)}
          onChange={(e) => handleChange(e.target.value)}
          className={clsx(
            'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
            showValidationError
              ? 'border-red-300 dark:border-red-600'
              : 'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
          )}
        >
          <option value="">Select an option...</option>
          {field.options?.map((option) => (
            <option key={option.id} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )
    }

    case 'color': {
      const colorValue = safeStringValue(value) || '#000000'
      return (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={colorValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md cursor-pointer"
          />
          <input
            type="text"
            value={colorValue}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="#000000"
            className={clsx(
              'flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
              showValidationError
                ? 'border-red-300 dark:border-red-600'
                : 'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
            )}
          />
        </div>
      )
    }
    case 'checkbox': {
      return (
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => handleChange(e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            {field.label}
          </span>
        </label>
      )
    }

    case 'file': {
      // Proper Dropzone and file preview implementation
      // value is a data URL string or null
      const currentFileUrl = typeof value === "string" && value ? value : null

      // For Dropzone's src, we want to show a "file" if there is a value, but Dropzone expects a File[] for its context.
      // However, for preview, we want to show the actual image from the data URL.
      // We'll reuse DropzoneEmptyState for the empty state and provide a custom preview when an image is present.

      return (
        <div className="space-y-2">
          {/* Dropzone for image upload */}
          <Dropzone
            accept={{ 'image/*': [] }}
            maxFiles={1}
            onDrop={handleDropzoneUpload}
            src={currentFileUrl ? [new File([], 'image')] : undefined} // src is only for DropzoneContent context, not used for file reading
            className={clsx(
              'w-full border-dashed border-2 rounded-lg transition-colors duration-200',
              showValidationError
                ? 'border-red-300 dark:border-red-600'
                : 'border-gray-300 dark:border-gray-600',
              'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
            )}
          >
            {currentFileUrl ? (
              // Use DropzoneContent to get the correct icon and layout, but override with our preview
              <div className="flex flex-col items-center w-full">
                <div className="w-full max-w-xs">
                  <Image
                    src={currentFileUrl}
                    alt="Uploaded background"
                    width={300}
                    height={169}
                    className="w-full h-auto rounded border object-cover"
                    style={{ aspectRatio: '16/9' }}
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Image selected
                </p>
                <span
                  role="button"
                  tabIndex={0}
                  className="mt-2 text-xs text-red-500 underline cursor-pointer"
                  onClick={() => handleChange(null)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleChange(null)
                    }
                  }}
                >
                  Remove image
                </span>
              </div>
            ) : (
              // Show the DropzoneEmptyState visual when no file is present
              <DropzoneEmptyState />
            )}
          </Dropzone>
        </div>
      )
    }

    // Handle all other field types with a default text input
    case 'code':
    case 'image':
    case 'url':
    case 'html':
    case 'radio':
    case 'markdown':
    case 'email':
    case 'date':
    case 'range':
    case 'ordered':
    case 'multiselect':
    case 'visual':
    default: {
      return (
        <input
          type="text"
          value={safeStringValue(value)}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={field.placeholder}
          className={clsx(
            'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
            showValidationError
              ? 'border-red-300 dark:border-red-600'
              : 'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
          )}
        />
      )
    }
  }
}
